<template>
  <div>
    <v-stepper v-model="step" flat v-show="step < 4">
      <v-stepper-header>
        <v-stepper-step :step="1">{{ $t('format') }}</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :step="2">{{ $t('columns') }}</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :step="3">{{ $t('values') }}</v-stepper-step>
        <v-divider></v-divider>
        <v-stepper-step :step="4">{{ $t('confirmation') }}</v-stepper-step>
      </v-stepper-header>
    </v-stepper>

    <div v-if="step === 1">
      <p>{{ $t('configuration') }}<code>{{ $t('false') }}</code> {{ $t('characters') }} <code>{{ $t('symbol') }}</code>
        {{ $t('error') }}</p>

      <v-row class="mb-2">
        <v-col>
          <v-select
              filled dense hide-details
              v-model="reading.csv.fields.delimiter"
              :items="reading.csv.values.delimiter"
              :label="$t('label.separator')"
          ></v-select>
        </v-col>
        <v-col>
          <v-select
              filled dense hide-details
              v-model="reading.csv.fields.enclosure"
              :items="reading.csv.values.enclosure"
              :label="$t('label.container')"
          ></v-select>
        </v-col>
        <v-col>
          <v-select
              filled dense hide-details
              v-model="reading.csv.fields.escape_character"
              :items="reading.csv.values.escape_character"
              :label="$t('label.escape')"
          ></v-select>
        </v-col>
        <v-col>
          <v-select
              filled dense hide-details
              v-model="reading.csv.fields.input_encoding"
              :items="reading.csv.values.input_encoding"
              :label="$t('label.encoding')"
          ></v-select>
        </v-col>
        <v-col>
          <v-select
              filled dense hide-details
              v-model="reading.ignore_headers"
              :items="[{text: 'Oui', value: true}, {text: 'Non', value: false}]"
              :label="$t('label.ignore')"
          ></v-select>
        </v-col>
      </v-row>

      <v-simple-table class="pi-4 overflow-visible" dense>
        <template v-slot:default>
          <tbody>
          <tr v-for="(rows, i) in verify.first_rows" :class="i === 0 && reading.ignore_headers ? 'grey--text' : ''"
              :key="i">
            <td v-for="(value, j) in rows" :key="i + j" style="min-width: 100px;">
              <span v-if="value === false" class="orange--text">{{ value }}</span>
              <span v-else>{{ value }}</span>
            </td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
    </div>
    <div v-else-if="step === 2">
      <p>{{ $t('select-columns') }}<code>{{ $t('keyword') }}</code>{{
          $t('history-positions')
        }}<code>{{ $t('keyword-position') }}</code>{{ $t('and') }} <code>
          {{ $t('position-date') }}</code>.</p>

      <v-simple-table class="pi-4 overflow-visible" dense>
        <template v-slot:default>
          <thead>
          <tr>
            <td v-for="(value, i) in verify.first_rows[0]" :key="i">
              <v-select
                  dense
                  style="min-width: 120px; max-width: 100%;"
                  filled
                  v-model="formatting.columns.fields[i]"
                  :items="formatting.columns.values">
              </v-select>
            </td>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(rows, i) in verify.first_rows" :key="i">
            <td v-for="(value, j) in rows" :key="i+j"
                :class="formatting.columns.fields[j] === false ? 'grey--text' : ''"
                style="max-width: 150px;">
              {{ value }}
            </td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
    </div>
    <div v-else-if="step === 3">
      <p>{{ $t('default-values') }}</p>
      <v-form v-model="filling.valid" class="mt-4" ref="form">
        <website-select
            :filled="true"
            :show-new="true"
            v-if="!computedHasWebsiteColumn"
            :value.sync="filling.fields.website">
        </website-select>
        <v-row v-if="!computedHasGeolangColumn">
          <v-col>
            <v-autocomplete
                filled
                v-model="filling.fields.country"
                :items="$locale.countriesItems"
                hide-no-data
                :label="$t('label.geolocation')"
                hide-details
            ></v-autocomplete>
          </v-col>
          <v-col>
            <v-autocomplete
                filled
                v-model="filling.fields.language"
                :items="$locale.languagesItems"
                hide-no-data
                :label="$t('label.language')"
                hide-details
            ></v-autocomplete>
          </v-col>
        </v-row>
      </v-form>
    </div>
    <div v-else-if="step === 4">
      <p>{{ $t('import') }}</p>

      <router-link to="account/subscriptions" target="_blank" class="text-decoration-none">
        <v-alert
            class="mt-4 mb-0"
            type="info"
            text
            border="left"
            icon="mdi-information-outline">
          {{ $t('keyword-quantity') }}
          ({{ maxKeywords }} {{$t('keywords') }}).
          <span v-if="$account.getSubscription('check-position').name === 'default'" class="text-decoration-underline">
            {{ $t('subscribe') }}
          </span>
          <span v-else class="text-decoration-underline">
             {{ $t('add') }}
          </span>
        </v-alert>
      </router-link>
    </div>
  </div>
</template>

<script>
import WebsiteSelect from '@/views/Common/Components/Selects/WebsiteSelect'
export default {
  name: 'ImportForm',
  components: { WebsiteSelect },
  props: ['filename', 'filetype', 'preset'],
  data() {
    return {
      step: 0,
      reading: {
        csv: {
          fields: {
            delimiter: this.filetype === 'csv' ? ';' : '\t',
            enclosure: '"',
            escape_character: '\\',
            input_encoding: 'UTF-8',
          },
          values: {
            delimiter: [
              {text: this.$t('semicolon'), value: ';'},
              {text: this.$t('comma'), value: ','},
              {text: this.$t('tabulation'), value: '\t'},
            ],
            enclosure: [
              {text: this.$t('quote'), value: '"'},
              {text: this.$t('apostrophe'), value: '\''},
            ],
            escape_character: [
              {text: this.$t('backslash'), value: '\\'}
            ],
            input_encoding: [
              {text: 'UTF-8', value: 'UTF-8'},
              {text: 'Latin-1 (ISO-8859-1)', value: 'ISO-8859-1'}
            ],
          }
        },
        ignore_headers: true,
      },
      formatting: {
        columns: {
          fields: [],
          values: [
            {text: this.$t('keyword'), value: 'keyword'},
            {text: this.$t('domain'), value: 'website'},
            {text: this.$t('keyword-position'), value: 'position'},
            {text: this.$t('position-date'), value: 'date'},
            {text: this.$t('geolang'), value: 'geolang'},
            {text: this.$t('ignore'), value: false},
          ]
        }
      },
      filling: {
        valid: false,
        fields: {
          website: '',
          country: 'en',
          language: 'en',
        },
        rules: {
          website: [v => this.computedHasWebsiteColumn ? true : (!!v || this.$t('required'))]
        }
      },
      verify: {
        first_rows: [],
        cols_count: 0,
        rows_count: 0,
      }
    }
  },
  watch: {
    'reading.csv.fields': {
      deep: true,
      handler() {
        this.fetch()
      }
    },
    step(step) {
      this.$emit('step', step)
    },
    computedValid(valid) {
      this.$emit('valid', valid)
    }
  },
  computed: {
    computedValid() {
      if (this.step === 1) {
        return this.verify.first_rows.length > 0
      } else if (this.step === 2) {
        return this.computedHasKeywordColumn
      } else if (this.step === 3) {
        return this.filling.valid
      }

      return true
    },
    computedHasKeywordColumn() {
      return this.formatting.columns.fields.findIndex(value => value === 'keyword') !== -1
    },
    computedHasWebsiteColumn() {
      return this.formatting.columns.fields.findIndex(value => value === 'website') !== -1
    },
    computedHasGeolangColumn() {
      return this.formatting.columns.fields.findIndex(value => value === 'geolang') !== -1
    },
    maxKeywords() {
      return this.$account.current.rules['check-position'].max_monitor
    },
  },
  methods: {
    async fetch() {
      try {
        const result = await this.$api.post('monitors/import/verify', {
          filename: this.filename,
          filetype: this.filetype,
          reading: {
            csv: this.reading.csv.fields,
          }
        })
        this.verify.first_rows = result.data.first_rows
        this.verify.cols_count = result.data.cols_count
        this.verify.rows_count = result.data.rows_count

        if (this.formatting.columns.fields.length !== this.verify.first_rows[0].length) {
          this.formatting.columns.fields = this.verify.first_rows[0].map(() => false)
        }
      } catch (e) {
        this.$ui.error(this.$t('error-analyze') + e.message)
      }
    },
    getValidStep(direction) {
      const isStepValid = (testStep) => {
        if (testStep === 1) {
          if (this.preset.reading.csv) return false
          else return this.filetype === 'csv' || this.filetype === 'tsv'
        } else if (testStep === 2) {
          if (this.preset.formatting.columns) return false
          else return true
        } else if (testStep === 3) {
          return !this.computedHasWebsiteColumn || !this.computedHasGeolangColumn
        } else {
          return true
        }
      }

      let step = this.step
      let isValid = false
      if (direction === 'next') {
        while (isValid === false) {
          step++
          isValid = isStepValid(step)
        }
      } else {
        while (isValid === false) {
          step--
          isValid = isStepValid(step)
        }
      }

      return step
    },
    next() {
      this.step = this.getValidStep('next')
    },
    back() {
      this.step = this.getValidStep('back')
    },
  },
  mounted() {
    this.fetch()

    this.filling.fields.country = this.$account.current.country
    this.filling.fields.language = this.$account.current.language

    if (this.preset) {
      if (this.preset.reading.csv !== null) this.reading.csv.fields = this.preset.reading.csv
      if (this.preset.formatting.columns !== null) this.formatting.columns.fields = this.preset.formatting.columns
      if (this.preset.reading.ignore_headers !== null) this.reading.ignore_headers = this.preset.reading.ignore_headers
      this.step = this.getValidStep('next')
      this.$emit('step', this.step)
    }
  }
}
</script>

<style>

</style>

<i18n>
{
	"en": {
		"add": "Add keywords to your subscription to import more keywords.",
		"and": "and",
		"apostrophe": "Apostrophe",
		"backslash": "Backslash",
		"characters": "or characters such as",
		"columns": "File columns",
		"comma": "Comma",
		"configuration": "Configure the format of your CSV file by inspecting the preview data below (only the first 50 rows are shown). Type values",
		"confirmation": "Confirmation of import",
		"default-values": "Fill in the default values that will be used to create each keyword.",
		"domain": "Website",
		"error": "may indicate the wrong choice of character encoding.",
		"error-analyze": "An error occurred while parsing your import file :",
		"false": "false",
		"format": "File format",
		"geolang": "Geoloc and Language",
		"hint-domain": "Enter or select the domain name of the website you want to monitor.",
		"history-positions": ". Your position history can be imported along with your keywords, just specify the columns",
		"ignore": "Ignored",
		"import": "Would you like to start importing keywords into your account? This action may take several minutes.",
		"keyword": "Keyword",
		"keyword-position": "Keyword position",
		"keyword-quantity": "Check that the quantity of keywords to import does not exceed the capacity of your account",
		"keywords": "keywords",
		"label": {
			"container": "Container",
			"domain": "Website",
			"encoding": "Character encoding",
			"escape": "Escape character",
			"geolocation": "Geolocation",
			"ignore": "Ignore the first row (header)",
			"language": "Language",
			"separator": "Separator"
		},
		"position-date": "Position date",
		"quote": "Quotation mark",
		"required": "The website to monitor is required.",
		"select-columns": "Select the columns that will be used for importing your data. You must at least specify the column",
		"semicolon": "Semicolon",
		"subscribe": "Subscribe to an offer to import more keywords.",
		"symbol": "Ã©",
		"tabulation": "Tabulation",
		"values": "Default values and formats"
	},
	"es": {
		"add": "Agregue palabras clave a su suscripción para importar más palabras clave.",
		"and": "y",
		"apostrophe": "Apóstrofe",
		"backslash": "Barra invertida",
		"characters": "o caracteres como",
		"columns": "Columnas de archivo",
		"comma": "Coma",
		"configuration": "Configure el formato de su archivo CSV inspeccionando los datos de vista previa a continuación (solo se muestran las primeras 50 filas). Valores de tipo",
		"confirmation": "Confirmación de importación",
		"default-values": "Complete los valores predeterminados que se utilizarán para crear cada palabra clave.",
		"domain": "Sitio Web",
		"error": "puede indicar una elección incorrecta de codificación de caracteres.",
		"error-analyze": "Se produjo un error al analizar su archivo de importación :",
		"false": "falso",
		"format": "Formato de archivo",
		"geolang": "Geoloc y lenguaje",
		"hint-domain": "Ingrese o seleccione el nombre de dominio del sitio web que desea monitorear.",
		"history-positions": ". Su historial de posiciones se puede importar junto con sus palabras clave, solo especifique las columnas",
		"ignore": "Ignorado",
		"import": "¿Le gustaría comenzar a importar palabras clave a su cuenta? Esta acción puede tardar varios minutos.",
		"keyword": "Palabra clave",
		"keyword-position": "Posición de la palabra clave",
		"keyword-quantity": "Verifique que la cantidad de palabras clave para importar no exceda la capacidad de su cuenta",
		"keywords": "palabras clave",
		"label": {
			"container": "Envase",
			"domain": "Pagina wen",
			"encoding": "Codificación de caracteres",
			"escape": "Caractere de escape",
			"geolocation": "Geolocalización",
			"ignore": "Ignorar la primera fila (titulo)",
			"language": "Lengua",
			"separator": "Separador"
		},
		"position-date": "Fecha de posición",
		"quote": "Comillas",
		"required": "Se requiere el sitio web a monitorear.",
		"select-columns": "Seleccione las columnas que se utilizarán para importar sus datos. Debes especificar al menos la columna",
		"semicolon": "Punto y coma",
		"subscribe": "Suscríbase a una oferta para importar más palabras clave.",
		"symbol": "Ã©",
		"tabulation": "Tabulación",
		"values": "Valores y formatos predeterminados"
	},
	"fr": {
		"add": "Ajoutez des mots clés à votre souscription pour importer plus de mots clés.",
		"and": "et",
		"apostrophe": "Apostrophe",
		"backslash": "Barre oblique inversée (backslash)",
		"characters": "ou les caractères tels que",
		"columns": "Colonnes du fichier",
		"comma": "Virgule",
		"configuration": "Configurez le format de votre fichier CSV en inspectant les données de prévisualisation ci-dessous (seules les 50 premières lignes sont affichées). Les valeurs de type ",
		"confirmation": "Confirmation de l'importation",
		"default-values": "Remplissez les valeurs par défaut qui seront utilisée pour créer chaque mot clé.",
		"domain": "Site Web",
		"error": "peuvent indiquer un mauvais choix d'encodage de caractères.",
		"error-analyze": "Une erreur est survenue lors de l'analyse de votre fichier d'import : ",
		"false": "false",
		"format": "Format du fichier",
		"geolang": "Géoloc et Langue",
		"hint-domain": "Saisissez ou selectionnez le nom de domaine du site web que vous souhaitez surveiller.",
		"history-positions": ". Votre historique des position peut être importé en même temps que vos mots-clés, spécifiez simplement les colonnes ",
		"ignore": "Ignoré",
		"import": "Souhaitez-vous lancer l'importation des mots clés sur votre compte ? Cette action peut prendre plusieurs minutes.",
		"keyword": "Mot-clé",
		"keyword-position": "Position du mot clé",
		"keyword-quantity": "Vérifiez que la quantité de mots clés à importer ne dépasse pas la capacité de votre compte",
		"keywords": "mots clés",
		"label": {
			"container": "Conteneur",
			"domain": "Site Web",
			"encoding": "Encodage des caractères",
			"escape": "Caractère d'échappement",
			"geolocation": "Géolocalisation",
			"ignore": "Ignorer la première ligne (en-tête)",
			"language": "Langue",
			"separator": "Séparateur"
		},
		"position-date": "Date de la position",
		"quote": "Guillemet",
		"required": "Le site web à surveiller est requis.",
		"select-columns": "Selectionnez les colonnes qui seront utilisées pour l'import de vos données. Vous devez au moins spécifier la colonne",
		"semicolon": "Point virgule",
		"subscribe": "Souscrivez à une offre pour importer plus de mots clés.",
		"symbol": "Ã©",
		"tabulation": "Tabulation",
		"values": "Valeurs par défaut et formats"
	},
	"it": {
		"add": "Aggiungi parole chiave al tuo abbonamento per importarne altre.",
		"and": "e",
		"apostrophe": "Apostrofo",
		"backslash": "Barra rovesciata",
		"characters": "o caratteri come",
		"columns": "Colonne del file",
		"comma": "Virgola",
		"configuration": "Configura il formato del tuo file CSV ispezionando i dati di anteprima qui sotto (vengono mostrate solo le prime 50 righe). Valori di tipo",
		"confirmation": "Conferma dell'importazione",
		"default-values": "Inserisci i valori predefiniti che verranno usati per creare ogni parola chiave.",
		"domain": "Sito web",
		"error": "potrebbe indicare una scelta errata della codifica dei caratteri.",
		"error-analyze": "Si è verificato un errore durante l'analisi del file di importazione :",
		"false": "falso",
		"format": "Formato del file",
		"geolang": "Geoloc e Lingua",
		"hint-domain": "Inserisci o seleziona il nome di dominio del sito web che vuoi monitorare.",
		"history-positions": ". La cronologia delle posizioni può essere importata insieme alle parole chiave, specifica le colonne",
		"ignore": "Ignorato",
		"import": "Vuoi avviare l'importazione delle parole chiave nel tuo account? Questa azione potrebbe richiedere diversi minuti.",
		"keyword": "Parola chiave",
		"keyword-position": "Posizione della parola chiave",
		"keyword-quantity": "Verifica che la quantità di parole chiave da importare non superi la capacità del tuo account",
		"keywords": "parole chiave",
		"label": {
			"container": "Contenitore",
			"domain": "Sito web",
			"encoding": "Codifica dei caratteri",
			"escape": "Carattere di escape",
			"geolocation": "Geolocalizzazione",
			"ignore": "Ignora la prima riga (intestazione)",
			"language": "Lingua",
			"separator": "Separatore"
		},
		"position-date": "Data della posizione",
		"quote": "Virgolette",
		"required": "Il sito web da monitorare è obbligatorio.",
		"select-columns": "Seleziona le colonne che verranno usate per importare i tuoi dati. Devi specificare almeno la colonna",
		"semicolon": "Punto e virgola",
		"subscribe": "Abbonati a un'offerta per importare più parole chiave.",
		"symbol": "é",
		"tabulation": "Tabulazione",
		"values": "Valori e formati predefiniti"
	},
	"pt": {
		"add": "Adicione palavras-chave à sua subscrição para importar mais palavras-chave.",
		"and": "e",
		"apostrophe": "Apóstrofo",
		"backslash": "Barra invertida",
		"characters": "ou caracteres como",
		"columns": "Colunas do ficheiro",
		"comma": "Vírgula",
		"configuration": "Configure o formato do seu ficheiro CSV inspecionando os dados de pré-visualização abaixo (apenas as primeiras 50 linhas são mostradas). Valores do tipo",
		"confirmation": "Confirmação da importação",
		"default-values": "Preencha os valores predefinidos que serão utilizados para criar cada palavra-chave.",
		"domain": "Site",
		"error": "pode indicar uma escolha errada de codificação de caracteres.",
		"error-analyze": "Ocorreu um erro ao analisar o seu ficheiro de importação :",
		"false": "false",
		"format": "Formato do ficheiro",
		"geolang": "Geoloc e Idioma",
		"hint-domain": "Introduza ou selecione o nome de domínio do site que deseja monitorizar.",
		"history-positions": ". O seu histórico de posições pode ser importado juntamente com as suas palavras-chave, basta especificar as colunas",
		"ignore": "Ignorado",
		"import": "Deseja iniciar a importação de palavras-chave para a sua conta? Esta ação pode demorar vários minutos.",
		"keyword": "Palavra-chave",
		"keyword-position": "Posição da palavra-chave",
		"keyword-quantity": "Verifique que a quantidade de palavras-chave a importar não excede a capacidade da sua conta",
		"keywords": "palavras-chave",
		"label": {
			"container": "Contentor",
			"domain": "Site",
			"encoding": "Codificação de caracteres",
			"escape": "Caractere de escape",
			"geolocation": "Geolocalização",
			"ignore": "Ignorar a primeira linha (cabeçalho)",
			"language": "Idioma",
			"separator": "Separador"
		},
		"position-date": "Data da posição",
		"quote": "Aspas",
		"required": "O site a monitorizar é obrigatório.",
		"select-columns": "Selecione as colunas que serão utilizadas para importar os seus dados. Deve especificar pelo menos a coluna",
		"semicolon": "Ponto e vírgula",
		"subscribe": "Subscreva uma oferta para importar mais palavras-chave.",
		"symbol": "Ã©",
		"tabulation": "Tabulação",
		"values": "Valores e formatos predefinidos"
	},
	"pl": {
		"add": "Dodaj słowa kluczowe do swojej subskrypcji, aby zaimportować więcej słów kluczowych.",
		"and": "i",
		"apostrophe": "Apostrof",
		"backslash": "Ukośnik odwrotny",
		"characters": "lub znaki takie jak",
		"columns": "Kolumny pliku",
		"comma": "Przecinek",
		"configuration": "Skonfiguruj format swojego pliku CSV, sprawdzając dane podglądu poniżej (wyświetlane są tylko pierwsze 50 wierszy). Wartości typu",
		"confirmation": "Potwierdzenie importu",
		"default-values": "Wypełnij wartości domyślne, które będą używane do tworzenia każdego słowa kluczowego.",
		"domain": "Strona internetowa",
		"error": "może wskazywać na błędny wybór kodowania znaków.",
		"error-analyze": "Wystąpił błąd podczas analizowania pliku importu :",
		"false": "false",
		"format": "Format pliku",
		"geolang": "Geolokalizacja i Język",
		"hint-domain": "Wpisz lub wybierz nazwę domeny strony internetowej, którą chcesz monitorować.",
		"history-positions": ". Historia Twoich pozycji może zostać zaimportowana razem ze słowami kluczowymi, wystarczy podać kolumny",
		"ignore": "Zignorowane",
		"import": "Czy chcesz rozpocząć importowanie słów kluczowych na swoje konto? Ta akcja może potrwać kilka minut.",
		"keyword": "Słowo kluczowe",
		"keyword-position": "Pozycja słowa kluczowego",
		"keyword-quantity": "Sprawdź, czy liczba słów kluczowych do zaimportowania nie przekracza pojemności Twojego konta",
		"keywords": "słowa kluczowe",
		"label": {
			"container": "Kontener",
			"domain": "Strona internetowa",
			"encoding": "Kodowanie znaków",
			"escape": "Znak ucieczki",
			"geolocation": "Geolokalizacja",
			"ignore": "Ignoruj pierwszy wiersz (nagłówek)",
			"language": "Język",
			"separator": "Separator"
		},
		"position-date": "Data pozycji",
		"quote": "Cudzysłów",
		"required": "Wymagana jest strona internetowa do monitorowania.",
		"select-columns": "Wybierz kolumny, które będą używane do importowania danych. Musisz co najmniej podać kolumnę",
		"semicolon": "Średnik",
		"subscribe": "Subskrybuj ofertę, aby zaimportować więcej słów kluczowych.",
		"symbol": "Ã©",
		"tabulation": "Tabulacja",
		"values": "Wartości domyślne i formaty"
	}
}
</i18n>
